<template>
  <calm-page extra-class="insure" :title="$t('lmg.insure')">
    <template slot="actions">
      <calm-button variant="primary" @click="handleCreate()">
        <i class="fas fa-plus mr-1"></i> {{ $t("new") }}
      </calm-button>
    </template>

    <!-- ═══════════════ HIERARCHY MODE TOGGLE (leaf-only / all) ═══════════════ -->
    <!-- Reskinned as Calm segmented chips. Bindings + handlers preserved:
         showEndNode drives which chip is active; handleListEndNode/handleListAll
         keep the exact store calls. -->
    <div class="calm__chips insure__modechips">
      <span class="calm__chips__label">{{ $t("lmg.insure") }}</span>
      <button
        type="button"
        class="calm__chipbtn"
        :class="{ 'is-active': showEndNode }"
        @click="handleListEndNode()"
      >
        <span class="calm__chipbtn__dot"></span>
        แสดงเฉพาะปลายสาย
      </button>
      <button
        type="button"
        class="calm__chipbtn"
        :class="{ 'is-active': !showEndNode }"
        @click="handleListAll()"
      >
        <span class="calm__chipbtn__dot"></span>
        แสดงทั้งหมด
      </button>
    </div>

    <!-- ═══════════════ SEARCH BAR ═══════════════ -->
    <calm-filter-bar :hide-actions="true">
      <calm-field :label="$t('search')">
        <input
          v-model="searchQuery"
          type="text"
          class="ed"
          :placeholder="$t('search')"
        />
      </calm-field>
    </calm-filter-bar>

    <!-- ═══════════════ TABLE (skin the existing el-table, §11) ═══════════════ -->
    <!-- The el-table is kept VERBATIM: columns/slots untouched so the ShowName
         hierarchy indentation (leading spaces in the data) renders exactly as
         before. We only re-skin via deep selectors in <style>. -->
    <calm-table
      :total="total"
      :from="from"
      :to="to"
      :per-page.sync="pagination.perPage"
      :current-page.sync="pagination.currentPage"
      :per-page-options="pagination.perPageOptions"
      :per-page-label="$t('per_page') !== 'per_page' ? $t('per_page') : 'ต่อหน้า'"
      :count-label="$t('data')"
      :to-label="$t('to')"
      :of-label="$t('of')"
      :empty-title="$t('no_data') !== 'no_data' ? $t('no_data') : 'ไม่พบข้อมูล'"
    >
      <el-table
        :data="queriedData"
        row-key="id"
        header-row-class-name="thead-light"
        @sort-change="sortChange"
        @selection-change="selectionChange"
      >
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          v-bind="column"
        >
        </el-table-column>

        <el-table-column :label="$t(`lmg.insure_data_type`)" min-width="180px">
          <div slot-scope="{  row }" class="d-flex">
            <template v-if="row.DataType==1"> {{ $t('lmg.insure_data_type_1') }}</template>
            <template v-if="row.DataType==2"> {{ $t('lmg.insure_data_type_2') }}</template>
            <template v-if="row.DataType==3"> {{ $t('lmg.insure_data_type_3') }}</template>
            <template v-if="row.DataType==4"> {{ $t('lmg.insure_data_type_4') }}</template>
            <template v-if="row.DataType==5"> {{ $t('lmg.insure_data_type_5') }}</template>
          </div>
        </el-table-column>

        <el-table-column :label="$t(`status`)" min-width="100px">
          <div slot-scope="{ $index, row }" class="d-flex">
            <span
              class="calm__pill"
              :class="row.IsActive==1 ? 'calm__pill--success' : 'calm__pill--neutral'"
            >
              <template v-if="row.IsActive==1">{{ $t('status_active') }}</template>
              <template v-else>{{ $t('status_not_active') }}</template>
            </span>
          </div>
        </el-table-column>

        <el-table-column
          min-width="150px"
          align="right"
          :label="$t('actions')"
        >
          <div slot-scope="{ $index, row }" class="insure__rowact">
            <calm-button
              variant="ghost"
              size="sm"
              icon-only
              @click="handleEdit($index, row)"
            >
              <i class="fas fa-pen"></i>
            </calm-button>
            <calm-button
              variant="danger"
              size="sm"
              icon-only
              v-if="showEndNode == true"
              @click="handleDelete($index, row)"
            >
              <i class="fas fa-trash"></i>
            </calm-button>
          </div>
        </el-table-column>
      </el-table>
    </calm-table>

    <!-- ═══════════════ ADD / EDIT MODAL (Level → Upper cascade preserved) ═══════════════ -->
    <calm-modal
      :show.sync="showAddModal"
      :title="$t('lmg.insure')"
      :show-footer="false"
    >
      <validation-observer v-slot="{handleSubmit}" ref="formValidator">
      <form class="insure__form" @submit.prevent="handleSubmit(onUpdateInsure)">

        <base-input
          :label="$t(`InsureLevel`)"
          input-classes="form-control-alternative new-event--title"
          v-if="md.ID ==0"
        >
          <el-select v-model="md.Level" :placeholder="$t('InsureLevel')" popper-class="calm-pop">
            <el-option
              v-for="option in insureLevelList"
              :key="option.ID"
              :label="option.Name"
              :value="option.ID"
            >
            </el-option>
          </el-select>
        </base-input>

        <base-input
          :label="$t(`lmg.insure_data_type`)"
          input-classes="form-control-alternative new-event--title"
        >
          <el-select v-model="md.DataType" :placeholder="$t('lmg.insure_data_type')" popper-class="calm-pop">
            <el-option
              v-for="option in insureDataType"
              :key="option.ID"
              :label="option.Name"
              :value="option.ID"
            >
            </el-option>
          </el-select>
        </base-input>


        <base-input
          :label="$t(`InsureUpper`)"
          input-classes="form-control-alternative new-event--title"
          v-if="md.Level != 1 && md.ID == 0"
        >
          <el-select v-model="md.Upper" :placeholder="$t('InsureUpper')" popper-class="calm-pop">
            <el-option
              v-for="option in UpperList"
              :key="option.ID"
              :label="option.ShowName"
              :value="option.ID"
            >
            </el-option>
          </el-select>
        </base-input>

        <base-input v-if="md.ID != 0" :label="$t('InsureLevel')" v-model="md.Level" :name="$t('name')" > </base-input>
        <base-input v-if="md.ID != 0 && showEndNode == true" :label="$t('InsureUpper')" v-model="md.UpperName" :name="$t('name')" disabled> </base-input>

        <base-input :label="$t('name')" v-model="md.Name" :name="$t('name')" rules="required"> </base-input>

        <base-input :label="$t('detail')" v-model="md.Detail" :name="$t('detail')" >
        </base-input>

         <base-input
          :label="$t(`status`)"
          input-classes="form-control-alternative new-event--title"
          v-if="md.ID != 0"
        >
          <el-select v-model="md.IsActive" :placeholder="$t('status')" popper-class="calm-pop">
            <el-option
              v-for="option in activeStatus"
              :key="option.ID"
              :label="option.Name"
              :value="option.ID"
            >
            </el-option>
          </el-select>
        </base-input>

        <div class="calm__modal__actions">
          <calm-button variant="ghost" @click="showAddModal = false">
            {{ $t("close") }}
          </calm-button>
          <calm-button variant="primary" native-type="submit">
            {{ $t("save") }}
          </calm-button>
        </div>
      </form>
      </validation-observer>
    </calm-modal>
  </calm-page>
</template>
<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "./PaginatedTables/InsurePaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  computed: {
    ...mapGetters({
      tableDataOrg: "Insure/getData",
      UpperList: "Insure/getUpper",
    }),
    tableData: function () {
      // console.log(this.$route.query.id);
      // if (this.$route.query.id != null) {
      //   return this.tableDataOrg.filter((m) => {
      //     return m.EnterpriseID == "" + this.$route.query.id;
      //   });
      // }

      return this.tableDataOrg;
    },
    tableColumns: function () {
      return [
        // {
        //   prop: "BranchName",
        //   label: this.$t("lmg.branch"),
        //   minWidth: 90,
        //   sortable: true,
        // },
        {
          // prop: "Name",
          prop: "ShowName",
          label: this.$t("lmg.insure"),
          minWidth: 400,
          sortable: true,
        },
        {
          prop: "Detail",
          label: this.$t("detail"),
          minWidth: 200,
          sortable: true,
        },
      ];
    },
  },
  watch: {
    "md.Level": {
      handler: function(val, oldVal) {
        this.md.Upper = ""

      if (val == 1) return
      this.listUpper({
        level : val - 1
      })
      },
      deep: true
    }
  },
  data() {
    return {
      selectedRows: [],
      Uppers: [],
      showAddModal: false,
      showEndNode: true,
      md: {
        ID: "",
        DataType:0,
        BranchName: "",
        BranchID: 0,
        Name: "",
        Detail: "",
        IsActive: 1,
        Level:"",
        Upper:"",
        UpperName: "",
      },
       activeStatus: [
        {
          ID: 1,
          Name: "ใช้งาน"
        },
        {
          ID: 0,
          Name: "ไม่ใช้งาน"
        }
      ],
      insureLevelList : [
        {ID:1,Name: "Level 1"},
        {ID:2,Name: "Level 2"},
        {ID:3,Name: "Level 3"},
        {ID:4,Name: "Level 4"},
        {ID:5,Name: "Level 5"},
        {ID:6,Name: "Level 6"},
      ],
      insureDataType : [
        {ID:1,Name: this.$t("lmg.insure_data_type_1")},
        {ID:2,Name: this.$t("lmg.insure_data_type_2")},
        {ID:3,Name: this.$t("lmg.insure_data_type_3")},
        {ID:4,Name: this.$t("lmg.insure_data_type_4")},
        {ID:5,Name: this.$t("lmg.insure_data_type_5")},
      ]
    };
  },
  mounted() {
    this.listData();
  },
  methods: {
    ...mapActions({
      createData: "Insure/createData",
      updateData: "Insure/updateData",
      deleteData: "Insure/deleteData",
      listData: "Insure/listData",
      listAll: "Insure/listAll",
      listUpper: "Insure/listUpper",
    }),

    handleListAll(){
      this.showEndNode = false
      this.listAll()
    },
    handleListEndNode(){
      this.showEndNode = true
      this.listData()
    },
    handleCreate() {
      this.$refs.formValidator.reset()
      this.showAddModal = true;
        this.md.ID= 0
        this.md.BranchName= ""
        this.md.BranchID= ""
        this.md.Name= ""
        this.md.Detail= ""
        this.md.IsActive= ""
        this.md.Level= 1
        this.md.Upper= ""
        this.md.DataType= 1
    },
    handleEdit(index, row) {
      this.showAddModal = true;
        this.md.ID= row.ID 
        this.md.BranchName= row.BranchName
        this.md.BranchID= row.BranchID
        this.md.Name= row.Name
        this.md.Detail= row.Detail
        this.md.IsActive= row.IsActive
        this.md.Level= row.Level
        this.md.UpperName= row.ShowName
        this.md.DataType= row.DataType
    },
    onLevelChange(){
      this.listUpper({
        level : this.md.InsureLevel
      })
    },
    onUpdateInsure() {
      this.showAddModal = false;

      if (this.md.ID == 0) {
        // create
        this.createData({
          level : this.md.Level,
          upperID : this.md.Upper,
          name: this.md.Name,
          detail: this.md.Detail,
          dataType: this.md.DataType,
        })
          .then((resp) => {
            this.showEndNode == true ? this.listData() : this.listAll()
            swal.fire({
              title: this.$t("create_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch((err) => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: this.getErrorText(err.response.data),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        // update

        this.updateData({
          ID : this.md.ID,
          name: this.md.Name,
          detail: this.md.Detail,
          branchID : this.md.BranchName,
          isActive: this.md.IsActive,
          dataType: this.md.DataType,
        })
          .then((resp) => {
            this.showEndNode == true ? this.listData() : this.listAll()
            swal.fire({
              title: this.$t("edit_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch((err) => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: this.getErrorText(err.response.data),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },
    handleDelete(index, row) {
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question") + row.Name,
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false,
        })
        .then((result) => {
          if (result.value) {
            this.deleteData({
              ID: row.ID,
            })
              .then((resp) => {
                this.listData();
                swal.fire({
                  title: this.$t("delete_success"),
                  text: ``,
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false,
                });
              })
              .catch((err) => {
                swal.fire({
                  title: this.$t(err.response.data.Msg),
                  text: this.getErrorText(err.response.data),
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false,
                });
              });
          }
        });
    },
    selectionChange(selectedRows) {
      this.selectedRows = selectedRows;
    },
  },
};
</script>
<style scoped>
/* Insurance catalogue — Calm skin. Everything is namespaced under .insure so
   nothing leaks to other pages. The el-table is kept VERBATIM (§11 "skin,
   don't rewrite") — columns, slots and the ShowName hierarchy-indentation
   column are untouched; we only re-style via deep selectors below. */

/* Hierarchy-mode chips sit just under the header, above the search bar. */
.insure__modechips {
  margin-top: 4px;
}

/* Row-action cell: keep the icon buttons inline + right-aligned. */
.insure__rowact {
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
}

/* Modal form: stacked inputs + action-row spacing. */
.insure__form >>> .form-group {
  margin-bottom: 16px;
}
.insure__form .calm__modal__actions {
  margin-top: 8px;
}

/* The free-text search box reuses the Kit's .ed input look. */
.insure >>> .ed {
  width: 100%;
}

/* Skin the existing <el-table> so it reads as a Calm surface without touching
   any column definitions or slots (§11). Deep selectors only. CRITICAL: we do
   NOT set any white-space rule on cells, so the ShowName leading-space
   indentation that visualises hierarchy depth renders exactly as before. */
.insure >>> .el-table {
  color: var(--c-text, #2c3e50);
  background: transparent;
}
.insure >>> .el-table th,
.insure >>> .el-table .thead-light th {
  background: var(--c-surface-soft, #f4f7fb);
  color: var(--c-text-soft, #5b6b7e);
  font-weight: 600;
  border-bottom: 1px solid var(--c-border, #e3e9f0);
}
.insure >>> .el-table td {
  border-bottom: 1px solid var(--c-border, #eef2f7);
}
.insure >>> .el-table::before {
  background: var(--c-border, #e3e9f0);
}
.insure >>> .el-table__row:hover > td {
  background: var(--c-surface-soft, #f7fafd) !important;
}
</style>
